import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BrandLogo from "@/components/BrandLogo";
import {
  Loader2, Award, Trophy, CheckCircle2, XCircle, ArrowRight, BookOpen,
  Flame, GraduationCap, Sparkles,
} from "lucide-react";
import { API } from "@/lib/api";

/** Public share view — no auth, snapshot-frozen content.
 *
 * Routes:
 *   /share/badge/:token
 *   /share/exam/:token        (kind = exam_result)
 *   /share/mock-exam/:token   (kind = mock_exam)
 *
 * Per phase 39 design: hybrid layout — same brand shell, headline content
 * swapped per kind. Privacy-safe: only display_handle, no PII.
 */
export default function PublicShare({ kind }) {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/share/${kind}/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Share link not found"));
  }, [kind, token]);

  // Dynamic OpenGraph + page title — fires once data arrives so social
  // re-fetches (Slack/Discord/iMessage unfurl, LinkedIn, modern FB) get a
  // per-share rich preview instead of the generic landing description.
  useEffect(() => {
    if (!data) return;
    const handle = data.display_handle || "a PrepMasters student";
    let title = "PrepMasters · Share";
    let desc  = "AI-assisted board exam review by Filipinos, for Filipinos.";
    if (kind === "streak") {
      const cs = data.current_streak || 0;
      title = `🔥 ${cs}-day streak on PrepMasters`;
      desc  = `${handle} is on a ${cs}-day daily-review streak${data.longest_streak ? ` (best: ${data.longest_streak} days)` : ""}. Catch up — your exam doesn't wait.`;
    } else if (kind === "badge") {
      title = `🏆 Earned: ${data.badge_name || "PrepMasters badge"}`;
      desc  = `${handle} just unlocked the "${data.badge_name}" badge on PrepMasters. ${data.badge_description || ""}`;
    } else if (kind === "exam_result") {
      title = `${data.passed ? "🎯 Passed" : "📝 Took"} a PrepMasters exam · ${Math.round(data.score_percentage || 0)}%`;
      desc  = `${handle} scored ${Math.round(data.score_percentage || 0)}% on ${data.module_name || "a module exam"} in ${data.course_name || "their course"}.`;
    } else if (kind === "mock_exam") {
      title = `${data.passed ? "🎯 PASSED" : "📝 Took"} mock board exam · ${Math.round(data.accuracy_percentage || 0)}%`;
      desc  = `${handle} took a full mock board exam on PrepMasters — ${Math.round(data.accuracy_percentage || 0)}% accuracy.`;
    }
    document.title = title;
    const setMeta = (selector, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const attr = selector.match(/\[(.+?)=/)[1];
        const key = selector.match(/="(.+?)"/)[1];
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', desc);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', desc);
    setMeta('meta[name="description"]', desc);
  }, [data, kind]);

  if (err) {
    return (
      <Wrapper>
        <Card className="p-8 text-center max-w-md" data-testid="share-not-found">
          <div className="mx-auto w-14 h-14 rounded-full bg-rose-100 grid place-items-center mb-3">
            <XCircle className="w-7 h-7 text-rose-600" />
          </div>
          <h1 className="font-display text-xl font-bold">Link not found</h1>
          <p className="text-sm text-slate-600 mt-1">{err}</p>
          <Link to="/" className="mt-4 inline-block">
            <Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="share-cta-go-home">
              Visit PrepMaster <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </Card>
      </Wrapper>
    );
  }

  if (!data) {
    return (
      <Wrapper>
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" data-testid="share-loading" />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="max-w-md w-full" data-testid={`share-public-${kind}`}>
        {kind === "badge" && <BadgeShareCard data={data} />}
        {kind === "exam_result" && <ExamShareCard data={data} />}
        {kind === "mock_exam" && <MockExamShareCard data={data} />}
        {kind === "streak" && <StreakShareCard data={data} />}

        <Card className="mt-4 p-5 text-center bg-gradient-to-br from-indigo-600 to-violet-600 text-white border-0 shadow-lg">
          <div className="text-xs uppercase tracking-[0.2em] opacity-80 font-bold">
            Want results like this?
          </div>
          <div className="font-display text-xl font-bold mt-1">
            Join PrepMaster — AI-assisted board exam review.
          </div>
          <Link to="/register" className="mt-4 inline-block">
            <Button
              size="lg"
              className="bg-white text-indigo-700 hover:bg-slate-100 font-bold"
              data-testid="share-cta-register"
            >
              Get Started Free <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        </Card>
      </div>
    </Wrapper>
  );
}

function Wrapper({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-indigo-50 to-violet-50 dark:from-slate-950 dark:via-indigo-950 dark:to-violet-950">
      <div className="mb-6">
        <BrandLogo size="lg" />
      </div>
      {children}
      <p className="text-[11px] text-slate-500 mt-6 text-center">
        Shared via <Link to="/" className="text-indigo-600 hover:underline font-semibold">PrepMaster</Link> ·
        AI-Assisted Board Exam Review
      </p>
    </div>
  );
}

// ============================================================
// Badge
// ============================================================
function BadgeShareCard({ data }) {
  const tierStyle = {
    bronze: "from-amber-700 to-amber-600",
    silver: "from-slate-500 to-slate-400",
    gold:   "from-yellow-500 to-yellow-400",
  }[data.badge_tier] || "from-indigo-600 to-violet-600";

  return (
    <Card className="overflow-hidden border-0 shadow-xl" data-testid="share-badge-card">
      <div className={`bg-gradient-to-br ${tierStyle} px-6 pt-8 pb-10 text-center text-white`}>
        <div className="mx-auto w-24 h-24 rounded-full bg-white/15 backdrop-blur grid place-items-center ring-4 ring-white/20 mb-4">
          <Award className="w-12 h-12" strokeWidth={1.5} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 font-bold">
          {data.badge_tier} Badge Unlocked
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">{data.badge_name}</h1>
        <p className="text-sm opacity-90 mt-2">{data.badge_description}</p>
      </div>
      <div className="p-5 bg-white dark:bg-slate-900 text-center">
        <div className="text-sm text-slate-700 dark:text-slate-300">
          Earned by <strong className="text-slate-900 dark:text-white">{data.display_handle}</strong>
        </div>
        {data.earned_at && (
          <div className="text-[11px] text-slate-500 mt-1">
            on {new Date(data.earned_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================
// Module Exam Result
// ============================================================
function ExamShareCard({ data }) {
  const pct = Math.round(data.score_percentage);
  const passed = data.passed;
  return (
    <Card className="overflow-hidden border-0 shadow-xl" data-testid="share-exam-card">
      <div className={`px-6 pt-8 pb-10 text-center text-white ${
        passed ? "bg-gradient-to-br from-emerald-600 to-teal-600" : "bg-gradient-to-br from-rose-600 to-orange-600"
      }`}>
        <div className="mx-auto w-20 h-20 rounded-full bg-white/15 backdrop-blur grid place-items-center ring-4 ring-white/20 mb-4">
          {passed ? <CheckCircle2 className="w-10 h-10" strokeWidth={1.75} /> : <BookOpen className="w-10 h-10" strokeWidth={1.75} />}
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 font-bold">
          Exam Result
        </div>
        <div className="font-display text-5xl sm:text-6xl font-extrabold mt-2 tabular-nums">{pct}%</div>
        <div className="text-sm opacity-90 mt-1">
          {data.correct} of {data.total} correct
        </div>
        <Badge className={`mt-3 ${passed ? "bg-white text-emerald-700" : "bg-white text-rose-700"} font-bold`}>
          {passed ? "PASSED" : "KEEP GOING"}
        </Badge>
      </div>
      <div className="p-5 bg-white dark:bg-slate-900 text-center space-y-1">
        <div className="text-sm font-bold text-slate-900 dark:text-white">{data.module_name}</div>
        <div className="text-xs text-slate-500 inline-flex items-center gap-1 justify-center">
          <GraduationCap className="w-3 h-3" /> {data.course_name}
        </div>
        <div className="text-[11px] text-slate-500 pt-1">
          By <strong className="text-slate-700 dark:text-slate-200">{data.display_handle}</strong>
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// Mock Exam
// ============================================================
function MockExamShareCard({ data }) {
  const pct = Math.round(data.accuracy_percentage);
  const passed = data.passed;
  return (
    <Card className="overflow-hidden border-0 shadow-xl" data-testid="share-mock-exam-card">
      <div className={`px-6 pt-8 pb-10 text-center text-white ${
        passed ? "bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500" : "bg-gradient-to-br from-slate-700 to-slate-800"
      }`}>
        <div className="mx-auto w-20 h-20 rounded-full bg-white/15 backdrop-blur grid place-items-center ring-4 ring-white/20 mb-4">
          <Trophy className="w-10 h-10" strokeWidth={1.5} />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 font-bold">
          Mock Board Exam
        </div>
        <div className="font-display text-5xl sm:text-6xl font-extrabold mt-2 tabular-nums">{pct}%</div>
        <div className="text-sm opacity-90 mt-1">
          {data.score_count} of {data.length} correct · {data.duration_minutes}-min simulation
        </div>
        <Badge className={`mt-3 ${passed ? "bg-white text-amber-700" : "bg-white text-slate-800"} font-bold`}>
          {passed ? `PASSED · ${data.passing_percent}% threshold` : "ATTEMPTED"}
        </Badge>
      </div>
      <div className="p-5 bg-white dark:bg-slate-900 text-center space-y-1">
        <div className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 justify-center">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" /> {data.course_name}
        </div>
        <div className="text-[11px] text-slate-500 pt-1">
          By <strong className="text-slate-700 dark:text-slate-200">{data.display_handle}</strong>
        </div>
      </div>
    </Card>
  );
}

// ============================================================
// Streak — UI Wave B viral acquisition asset
// ============================================================
function StreakShareCard({ data }) {
  const cs = data.current_streak || 0;
  const ls = data.longest_streak || cs;
  const at = data.answered_today || 0;
  return (
    <Card className="overflow-hidden border-0 shadow-xl" data-testid="share-streak-card">
      <div className="relative px-6 pt-8 pb-10 text-center text-white bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 0% 100%, rgba(255,255,255,0.25) 0, transparent 55%), radial-gradient(circle at 100% 0%, rgba(0,0,0,0.2) 0, transparent 55%)",
          }}
        />
        <div className="relative">
          <div className="mx-auto w-20 h-20 rounded-full bg-white/15 backdrop-blur grid place-items-center ring-4 ring-white/20 mb-4">
            <Flame className="w-10 h-10 pm-flame-pulse" strokeWidth={1.5} />
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 font-bold">
            Daily Streak
          </div>
          <div className="font-display text-7xl sm:text-8xl font-black mt-2 tabular-nums leading-none drop-shadow-lg">
            {cs}
          </div>
          <div className="text-sm opacity-90 mt-2 font-semibold">
            {cs === 1 ? "day" : "days"} of consistent review
          </div>
          {at > 0 && (
            <div className="mt-3 inline-block bg-white/15 backdrop-blur rounded-full px-3 py-1 text-xs font-bold">
              {at} card{at !== 1 ? "s" : ""} answered today
            </div>
          )}
        </div>
      </div>
      <div className="p-5 bg-white dark:bg-slate-900 text-center space-y-1">
        <div className="text-sm font-bold text-slate-900 dark:text-white inline-flex items-center gap-1.5 justify-center">
          <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          Personal best: <span className="tabular-nums">{ls}</span> {ls === 1 ? "day" : "days"}
        </div>
        <div className="text-[11px] text-slate-500 pt-1">
          By <strong className="text-slate-700 dark:text-slate-200">{data.display_handle}</strong>
        </div>
      </div>
    </Card>
  );
}
